import type { Article, ContentfulAnimal } from '@/types/contentful'
import { generateArticlePath, generateCategoryPath } from './slugResolver'
import { fixImageUrlProtocol } from '@/utils/contentfulHelpers'

// Extract text from Rich Text content
export const extractTextFromRichText = (content: any): string => {
  // Handle null, undefined, or non-array content
  if (!content) return ''

  // If content is an object with a content property, use that
  if (typeof content === 'object' && content.content && Array.isArray(content.content)) {
    return extractTextFromRichText(content.content)
  }

  // If content is not an array, return empty string
  if (!Array.isArray(content)) return ''

  return content
    .map((node) => {
      if (node.nodeType === 'text') {
        return node.value || ''
      }
      if (node.content) {
        const innerText = extractTextFromRichText(node.content)
        // Add line break after paragraphs and list items
        if (node.nodeType === 'paragraph' || node.nodeType === 'list-item') {
          return innerText + '\n'
        }
        return innerText
      }
      return ''
    })
    .join('')
}

// Extract facts from secondary content (TextBlock entries)
export const extractFactsFromSecondary = (secondary: any[], includes: any): string[] => {
  if (!secondary || !includes?.Entry) return []

  // Get the IDs of the secondary entries for this specific animal
  const secondaryIds = secondary.map((item: any) => item.sys.id)

  // Find the facts block that belongs to this specific animal
  const factsBlock = includes.Entry.find(
    (entry: any) =>
      entry.sys.contentType?.sys.id === 'textBlock' &&
      entry.fields?.blockType &&
      Array.isArray(entry.fields.blockType) &&
      entry.fields.blockType.includes('fakta') &&
      secondaryIds.includes(entry.sys.id),
  )

  if (!factsBlock || !factsBlock.fields?.body?.content) return []

  const factsText = extractTextFromRichText(factsBlock.fields.body.content)
  return factsText.split('\n').filter((fact) => fact.trim())
}

// Extract questions from secondary content (TextBlock entries)
const extractQuestionsFromSecondary = (secondary: any[], includes: any): any => {
  if (!secondary || !includes?.Entry) return { title: '', preamble: '', questions: [] }

  // Get the IDs of the secondary entries for this specific animal
  const secondaryIds = secondary.map((item: any) => item.sys.id)

  // Find the questions block that belongs to this specific animal
  const questionsBlock = includes.Entry.find(
    (entry: any) =>
      entry.sys.contentType?.sys.id === 'textBlock' &&
      entry.fields?.blockType &&
      Array.isArray(entry.fields.blockType) &&
      entry.fields.blockType.includes('värderingsfrågor') &&
      secondaryIds.includes(entry.sys.id),
  )

  if (!questionsBlock || !questionsBlock.fields?.body?.content)
    return { title: '', preamble: '', questions: [] }

  const questionsText = extractTextFromRichText(questionsBlock.fields.body.content)

  // Split by newlines and clean up
  const questions = questionsText
    .split(/\n/)
    .map((question) => question.replace(/^[-•*]\s*/, '').trim())
    .filter((question) => question.length > 0)

  return {
    title: questionsBlock.fields.rubrik || '',
    preamble: '',
    questions: questions,
  }
}

// Extract animal rights content from secondary content (TextBlock entries)
const extractAnimalRightsFromSecondary = (secondary: any[], includes: any): any => {
  if (!secondary || !includes?.Entry) return { title: '', preamble: '', body: '' }

  // Get the IDs of the secondary entries for this specific animal
  const secondaryIds = secondary.map((item: any) => item.sys.id)

  // Find the animal rights block that belongs to this specific animal
  const rightsBlock = includes.Entry.find(
    (entry: any) =>
      entry.sys.contentType?.sys.id === 'textBlock' &&
      entry.fields?.blockType &&
      Array.isArray(entry.fields.blockType) &&
      entry.fields.blockType.includes('djurens rätt') &&
      secondaryIds.includes(entry.sys.id),
  )

  if (!rightsBlock || !rightsBlock.fields?.body?.content)
    return { title: '', preamble: '', body: '' }

  // Use the rubrik field as title
  const title = rightsBlock.fields.rubrik || ''

  // Extract the full text content
  const fullText = extractTextFromRichText(rightsBlock.fields.body.content)

  // Split by single newlines to separate paragraphs (Rich Text uses single \n)
  const paragraphs = fullText.split('\n').filter((para) => para.trim())

  let preamble = ''
  let body = ''

  if (paragraphs.length >= 1) {
    preamble = paragraphs[0]?.trim() || ''
  }

  if (paragraphs.length >= 2) {
    // Join remaining paragraphs as body, preserving paragraph breaks
    body = paragraphs.slice(1).join('\n\n').trim()
  }

  return {
    title: title,
    preamble: preamble,
    body: body,
  }
}

// Extract text and links from Rich Text content for sources
const extractSourcesFromRichText = (content: any[]): Array<{ sourceName: string; url: string }> => {
  const sources: Array<{ sourceName: string; url: string }> = []
  let currentSource = { sourceName: '', url: '' }

  const processNode = (node: any) => {
    if (node.nodeType === 'text') {
      currentSource.sourceName += node.value || ''
    } else if (node.nodeType === 'hyperlink') {
      // Extract only the link text (source name), not the URL
      const linkText = node.content?.map((c: any) => c.value || '').join('') || ''
      currentSource.sourceName += linkText
      currentSource.url = node.data?.uri || ''
    } else if (node.nodeType === 'paragraph') {
      // Start a new source for each paragraph
      if (currentSource.sourceName.trim()) {
        sources.push({ ...currentSource })
        currentSource = { sourceName: '', url: '' }
      }
      if (node.content) {
        node.content.forEach(processNode)
      }
    } else if (node.content) {
      node.content.forEach(processNode)
    }
  }

  content.forEach(processNode)

  // Add the last source if it has content
  if (currentSource.sourceName.trim()) {
    sources.push({ ...currentSource })
  }

  // Clean up source names by removing URLs in parentheses
  const cleanedSources = sources.map((source) => {
    let cleanSourceName = source.sourceName
    // Remove URL in parentheses pattern: "Name (URL)" -> "Name"
    cleanSourceName = cleanSourceName.replace(/\s*\(https?:\/\/[^)]+\)\s*/, '')
    // Remove trailing spaces and parentheses
    cleanSourceName = cleanSourceName.replace(/\s*\(\s*$/, '').trim()

    return {
      sourceName: cleanSourceName,
      url: source.url,
    }
  })

  return cleanedSources.filter((source) => source.sourceName.trim())
}

// Extract sources from secondary content (TextBlock entries)
const extractSourcesFromSecondary = (secondary: any[], includes: any): any[] => {
  if (!secondary || !includes?.Entry) return []

  // Get the IDs of the secondary entries for this specific animal
  const secondaryIds = secondary.map((item: any) => item.sys.id)

  // Find the sources block that belongs to this specific animal
  const sourcesBlock = includes.Entry.find(
    (entry: any) =>
      entry.sys.contentType?.sys.id === 'textBlock' &&
      entry.fields?.blockType &&
      Array.isArray(entry.fields.blockType) &&
      entry.fields.blockType.includes('källa') &&
      secondaryIds.includes(entry.sys.id),
  )

  if (!sourcesBlock || !sourcesBlock.fields?.body?.content) return []

  // Extract sources with links from the body content
  const sources = extractSourcesFromRichText(sourcesBlock.fields.body.content)

  // Convert to the expected format
  return sources.map((source) => ({
    sourceName: source.sourceName,
    url: source.url,
    authorOrInstitution: '',
    urlDate: null,
  }))
}

// Simple transformer that uses useRichText for body content
export const transformArticleToAnimal = (article: Article, includes?: any): ContentfulAnimal => {
  const imageUrl = article.fields.cloudinaryImage
    ? fixImageUrlProtocol(article.fields.cloudinaryImage[0]?.url)
    : ''

  // For now, use title as species and extract basic info from body
  // In a real implementation, you might want to add these as separate fields in Contentful
  const firstContent = article.fields.body?.content?.[0]?.content?.[0]
  const bodyText = firstContent && 'value' in firstContent ? firstContent.value : ''

  // Extract basic info from the body text (this is a simplified approach)
  const speciesMatch = bodyText.match(/Art:\s*([^\n]+)/)
  const scientificNameMatch = bodyText.match(/Vetenskapligt namn:\s*([^\n]+)/)
  const familyMatch = bodyText.match(/Familj:\s*([^\n]+)/)
  const habitatMatch = bodyText.match(/Habitat:\s*([^\n]+)/)

  // Extract facts from secondary content
  const facts = extractFactsFromSecondary(article.fields.secondary || [], includes)

  // Extract questions from secondary content
  const questionsData = extractQuestionsFromSecondary(article.fields.secondary || [], includes)

  // Extract animal rights content from secondary content
  const animalRights = extractAnimalRightsFromSecondary(article.fields.secondary || [], includes)

  // Extract sources from secondary content
  const sources = extractSourcesFromSecondary(article.fields.secondary || [], includes)

  return {
    _id: article.sys.id,
    _path: generateArticlePath(article),
    _dir: 'djurfakta',
    _draft: false,
    _partial: false,
    _locale: 'sv',
    _type: 'contentful',
    _source: 'contentful',
    _file: `${article.fields.slug}.md`,
    _extension: 'md',
    slug: article.fields.slug,
    body: article.fields.body ? JSON.stringify(article.fields.body) : '',
    title: article.fields.title,
    name: article.fields.title,
    kicker: article.fields.kicker || '',
    species: speciesMatch?.[1]?.trim() || article.fields.title,
    scientificName: scientificNameMatch?.[1]?.trim() || '',
    family: familyMatch?.[1]?.trim() || '',
    habitat: habitatMatch?.[1]?.trim() || '',
    image: imageUrl,
    preamble: article.fields.preamble,
    author: '',
    metadescription: article.fields.preamble,
    labels: [],
    habitatFilters: [],
    seeFilters: [],
    slider: article.fields.slider,
    'facts-block': {
      facts: facts,
    },
    'question-block': questionsData,
    'source-block': {
      source: sources,
    },
    'animal-rights-block': animalRights,
    published: true,
  }
}

// Transform Contentful Article to ContentfulAnimal format
export const transformArticleToArticle = (article: Article) => {
  return {
    _id: article.sys.id,
    _path: generateArticlePath(article),
    title: article.fields.title,
    preamble: article.fields.preamble,
    image: article.fields.cloudinaryImage
      ? fixImageUrlProtocol(article.fields.cloudinaryImage[0]?.url)
      : '',
    date: article.fields.date,
    author: '',
    published: true,
  }
}

/**
 * Transform Contentful category to match the expected format
 */
export const transformContentfulCategory = (category: any) => {
  return {
    title: category.fields.title,
    _path: generateCategoryPath(category.fields.slug),
    image: category.fields.cloudinaryImage
      ? fixImageUrlProtocol(category.fields.cloudinaryImage[0]?.url)
      : null,
  }
}

/**
 * Transform Contentful portal to match the expected format
 */
export const transformContentfulPortal = (portal: any) => {
  return {
    title: portal.fields.title,
    preamble: portal.fields.preamble,
    metadescription: portal.fields.metadescription || portal.fields.preamble,
    image: portal.fields.cloudinaryImage
      ? fixImageUrlProtocol(portal.fields.cloudinaryImage[0]?.url)
      : '',
    tags: {
      links: [],
      moreLink: null,
    },
    mascot: portal.fields.mascot || [],
    upperBlocks: portal.fields.upperBlocks || [],
    lowerBlocks: portal.fields.lowerBlocks || [],
    includes: portal.includes,
    kicker: portal.kicker,
    urlText: portal.fields.urlText,
    url: portal.fields.url,
  }
}

/**
 * Transform article to include form blocks for article-template
 * Universal function that can be used across all pages
 */
export function transformArticleWithFormBlocks(article: any, formData: any): any {
  if (!article) return null

  const transformedArticle: any = { ...article }

  // Add blocks array if form data exists
  if (formData) {
    // Determine template type from formType array
    const formType = formData.fields.formType?.[0] || 'App'
    let templateType = 'form' // default

    switch (formType) {
      case 'App':
        templateType = 'form'
        break
      case 'Miniklubbar':
        templateType = 'club_form'
        break
      case 'Tävling':
        templateType = 'competition_form'
        break
    }

    transformedArticle.blocks = [
      {
        _template: templateType,
        ...formData.fields,
      },
    ]
  }

  return transformedArticle
}

/**
 * Transform more articles data for sidebar
 */
export const transformMoreArticles = (
  articlesResponse: any,
  currentSlug: string,
  limit: number = 40,
) => {
  if (!articlesResponse?.items) return []

  return articlesResponse.items
    .filter((article: any) => article.fields.slug !== currentSlug)
    .map((article: any) => ({
      title: article.fields.title,
      _path: generateArticlePath(article),
    }))
    .slice(0, limit)
}
